// #2366 — scribe presence, announcer-fe's own DISPLAY decision.
//
// The read endpoint (#2363, GET /presence) returns every seat seen inside a
// bounded window (PRESENCE_MAX_AGE_SECONDS, one hour), each tagged with the
// server's own `live` fact (#2370): was lastSeenAt within PRESENCE_TTL_SECONDS
// of the server clock. So a seat whose scribe went quiet two minutes ago comes
// back as a row with live:false, distinguishable from a seat nobody ever
// connected to (no row at all), and that survives a page reload. This app
// keeps NO memory of earlier polls: each poll's response is the whole truth.
//
// SCRIBE_QUIET_THRESHOLD_SECONDS is a per-app judgement call (the card's own
// "Show elapsed time, not just a colour" section: "the threshold for 'gone'
// is the announcer's judgement to make in the moment"), deliberately
// SEPARATE from score-common's PRESENCE_TTL_SECONDS even though they share
// a value today — one is a storage/read cutoff, the other is a display
// urgency threshold, and nothing requires them to move together.
// Starting value from Jaybill's card comment (2026-09-07): "red at about 60
// seconds of silence while a ride is up... tighter than scoreadmin's
// because the announcer is deciding whether to call the next rider. That is
// a starting point to confirm against a real ring, not a researched figure
// — flag it for Jaybill if it feels wrong in practice."
export const SCRIBE_QUIET_THRESHOLD_SECONDS = 60;

// "12s" / "6m" / "1h 5m" — coarse on purpose, same style as OrderOfGo's own
// formatCountdown (a stopwatch reading, not a race clock). Never negative:
// a clock skew or a poll landing a beat before `now` catches up floors to 0.
export function formatElapsedSeconds(totalSeconds) {
  const seconds = Math.max(0, Math.floor(totalSeconds));
  if (seconds < 60) return `${seconds}s`;
  const totalMinutes = Math.floor(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}

// Indexes a batch of listPresence() entries (score-common's
// toPresenceResponseEntry shape: { seat, lastSeenAt, live, scribeName, ... })
// by seat. Pure -- no I/O, no Date.now() -- so it's fully unit-testable and
// the poll effect stays a thin caller. Several instances can claim one seat
// (two tabs, a tablet swap that leaves a stale row beside a live one): the
// freshest lastSeenAt wins. Missing/malformed entries are skipped, and an
// empty response is a normal "nobody has been here lately" answer.
export function indexPresenceBySeat(entries) {
  const bySeat = {};
  if (!Array.isArray(entries)) return bySeat;
  for (const entry of entries) {
    if (!entry?.seat || !entry.lastSeenAt) continue;
    const lastSeenAtMs = new Date(entry.lastSeenAt).getTime();
    if (!Number.isFinite(lastSeenAtMs)) continue;
    const existing = bySeat[entry.seat];
    if (existing && existing.lastSeenAtMs >= lastSeenAtMs) continue;
    bySeat[entry.seat] = {
      lastSeenAtMs,
      scribeName: entry.scribeName ?? null,
      live: entry.live ?? null,
    };
  }
  return bySeat;
}

// The render-ready rows for the current class's presence strip: one per
// OBSERVED seat (never a seat this session has not seen — see file header),
// sorted for a stable on-screen order, each carrying the elapsed seconds
// AND the derived "quiet" boolean so a caller never has to duplicate the
// threshold comparison (AC6 — one named constant, one place it's applied).
export function presenceRowsFor(observed, nowMs) {
  return Object.entries(observed || {})
    .map(([seat, { lastSeenAtMs, live }]) => {
      const elapsedSeconds = Math.max(0, Math.floor((nowMs - lastSeenAtMs) / 1000));
      return {
        seat,
        elapsedSeconds,
        // The server's `live:false` is authoritative (it is the write-time TTL
        // fact), and our own threshold still applies between polls as the
        // clock ticks past it.
        quiet: live === false || elapsedSeconds >= SCRIBE_QUIET_THRESHOLD_SECONDS,
      };
    })
    .sort((a, b) => a.seat.localeCompare(b.seat));
}
